import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, Newspaper, Calendar, ArrowLeft, Share2 } from "lucide-react";
import { LLink } from "@/i18n/LLink";
import { fetchNewsBySlug, TYPE_LABELS } from "@/api/news";
import Seo from "@/components/Seo";
import { useLang } from "@/i18n/LangContext";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const imageUrl = (v) => {
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  if (v.startsWith("/api/")) return `${BACKEND_URL}${v}`;
  return v;
};

const formatDate = (iso, lang = "nl") => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(
      lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "nl-NL",
      { day: "numeric", month: "long", year: "numeric" },
    );
  } catch {
    return iso;
  }
};

export const NieuwsDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const data = await fetchNewsBySlug(slug);
        if (!cancelled) setPost(data);
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) setNotFound(true);
          setPost(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="nieuws-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E9CD6]" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="nieuws-detail-not-found">
        <div className="max-w-md text-center">
          <Newspaper className="w-10 h-10 text-[#8AA3B8] mx-auto mb-4" />
          <h1 className="text-2xl font-[Poppins] font-bold text-[#173A5E] mb-2">
            Bericht niet gevonden
          </h1>
          <p className="text-[#5A7594] mb-6">Het bericht bestaat niet (meer) of is niet gepubliceerd.</p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#173A5E] hover:bg-[#0F2A46] text-white font-[Poppins] font-semibold text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Terug
          </button>
        </div>
      </div>
    );
  }

  const img = imageUrl(post.image);
  const shareUrl = `${BACKEND_URL}/api/share${lang === "nl" ? "" : `/${lang}`}/nieuws/${post.slug}`;

  // JSON-LD Article / NewsArticle voor zoekmachines
  const SITE_URL = process.env.REACT_APP_SITE_URL || "";
  const canonicalUrl = `${SITE_URL}${lang === "nl" ? "" : "/" + lang}/nieuws/${post.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": post.type === "persbericht" ? "NewsArticle" : "Article",
    headline: post.title,
    url: canonicalUrl,
    ...(post.excerpt && { description: post.excerpt, articleBody: post.excerpt }),
    ...(img && { image: img }),
    ...(post.published_at && { datePublished: post.published_at }),
    ...(post.updated_at && { dateModified: post.updated_at }),
    author: { "@type": "Organization", name: post.author || "Lemmer Leeft" },
    publisher: {
      "@type": "Organization",
      name: "Lemmer Leeft",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logos/header-lemmer-leeft.png` },
    },
  };

  return (
    <article data-testid="nieuws-detail-page" className="bg-white">
      <Seo
        title={`${post.title} — Lemmer Leeft`}
        description={post.excerpt}
        lang={lang}
        image={post.image}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <header className="relative bg-gradient-to-b from-[#173A5E] to-[#0F2A46] text-white">
        <div className="ll-container py-12 sm:py-16">
          <LLink
            to="/nieuws"
            data-testid="back-to-nieuws"
            className="inline-flex items-center gap-1.5 text-[#6BC0E8] hover:text-white font-[Poppins] font-semibold text-sm mb-5"
          >
            <ArrowLeft className="w-4 h-4" /> Alle berichten
          </LLink>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span
              data-testid="nieuws-type-label"
              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                post.type === "persbericht"
                  ? "bg-[#F4E9D8] text-[#8B6914]"
                  : "bg-[#E1F0FA] text-[#0F5C8A]"
              }`}
            >
              {TYPE_LABELS[post.type] || post.type}
            </span>
            {post.published_at && (
              <span className="inline-flex items-center gap-1 text-white/80 text-sm">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(post.published_at, lang)}
              </span>
            )}
            {post.author && (
              <span className="text-white/70 text-sm">· {post.author}</span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-[Poppins] font-bold leading-tight text-white">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="mt-4 text-white/85 font-[Inter] text-base sm:text-lg">
              {post.excerpt}
            </p>
          )}
        </div>
      </header>

      {img && (
        <div className="ll-container -mt-6 sm:-mt-8 mb-8 sm:mb-10">
          <div className="rounded-2xl overflow-hidden shadow-xl aspect-[16/9] bg-[#F4F8FB]">
            <img
              src={img}
              alt={post.title}
              data-testid="nieuws-featured-image"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}

      <div className="ll-container pb-16 sm:pb-24">
        <div>
          {/* Body: server-gesanitiseerde HTML — veilig te injecteren */}
          <div
            className="ll-prose"
            data-testid="nieuws-body"
            dangerouslySetInnerHTML={{ __html: post.body_html || "" }}
          />

          <div className="mt-10 pt-6 border-t border-[#E4EEF5] flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onCopyLink}
              data-testid="nieuws-share-copy"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F4F8FB] hover:bg-[#E4EEF5] text-[#173A5E] font-[Poppins] font-semibold text-sm"
            >
              <Share2 className="w-4 h-4" /> {copied ? "Link gekopieerd!" : "Deel bericht"}
            </button>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(post.title + " — " + shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nieuws-share-whatsapp"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366] hover:bg-[#1FBE59] text-white font-[Poppins] font-semibold text-sm"
            >
              WhatsApp
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nieuws-share-facebook"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2] hover:bg-[#0F6BDC] text-white font-[Poppins] font-semibold text-sm"
            >
              Facebook
            </a>
          </div>
        </div>
      </div>
    </article>
  );
};

export default NieuwsDetailPage;
